/**
 * Construção do salt do DPGen (`dpgen:v1`).
 *
 * O salt é público: ele só combina serviço, login, contador e política. O único
 * segredo do sistema é a senha-mestra, que entra como *password* do Argon2id.
 */

import {
  ALGO_VERSION,
  FIELD_SEPARATOR,
  MAX_COUNTER,
  MAX_LENGTH,
  MIN_COUNTER,
  MIN_LENGTH,
  type Policy,
} from './spec'

/**
 * A senha-mestra é normalizada apenas em forma Unicode. Sem trim e sem
 * lowercase: espaços e maiúsculas são parte do segredo que o usuário digitou.
 */
export function normalizeSecret(master: string): string {
  return master.normalize('NFKC')
}

/**
 * Serviço e login são normalizados de forma agressiva para que "GitHub.com ",
 * "github.com" e "GITHUB.COM" produzam a mesma senha — erros de digitação
 * casuais não devem custar o acesso à conta.
 */
export function normalizeField(value: string): string {
  return value.normalize('NFKC').trim().toLowerCase()
}

/**
 * Representação canônica da política. Entra no salt para que qualquer mudança
 * de parâmetro gere uma senha totalmente independente, sem correlação com as
 * variantes anteriores da mesma conta.
 */
export function policyTag(policy: Policy): string {
  const flags = [policy.lower, policy.upper, policy.digits, policy.symbols]
    .map((on) => (on ? '1' : '0'))
    .join('')
  return [
    String(policy.length),
    flags,
    policy.symbolSet,
    policy.excludeAmbiguous ? '1' : '0',
  ].join('|')
}

export function assertValidCounter(counter: number): void {
  if (!Number.isInteger(counter) || counter < MIN_COUNTER || counter > MAX_COUNTER) {
    throw new RangeError(
      `contador inválido: precisa ser um inteiro entre ${MIN_COUNTER} e ${MAX_COUNTER}`,
    )
  }
}

export function assertValidPolicy(policy: Policy): void {
  if (
    !Number.isInteger(policy.length) ||
    policy.length < MIN_LENGTH ||
    policy.length > MAX_LENGTH
  ) {
    throw new RangeError(
      `comprimento inválido: precisa ser um inteiro entre ${MIN_LENGTH} e ${MAX_LENGTH}`,
    )
  }
  if (!policy.lower && !policy.upper && !policy.digits && !policy.symbols) {
    throw new RangeError('pelo menos um conjunto de caracteres precisa estar habilitado')
  }
}

/** Bytes canônicos que serão passados pelo SHA-256. */
export function saltPreimage(
  service: string,
  login: string,
  counter: number,
  policy: Policy,
): Uint8Array {
  assertValidCounter(counter)
  assertValidPolicy(policy)
  const parts = [
    ALGO_VERSION,
    normalizeField(service),
    normalizeField(login),
    String(counter),
    policyTag(policy),
  ]
  return new TextEncoder().encode(parts.join(FIELD_SEPARATOR))
}

/** SHA-256 da pré-imagem canônica → 32 bytes de salt. */
export async function buildSalt(
  service: string,
  login: string,
  counter: number,
  policy: Policy,
): Promise<Uint8Array> {
  const preimage = saltPreimage(service, login, counter, policy)
  const digest = await crypto.subtle.digest('SHA-256', preimage as BufferSource)
  return new Uint8Array(digest)
}
